import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Layout } from '@/components/layout/Layout';
import { ReferNewPatientCTA } from '@/components/provider-dashboard/ReferNewPatientCTA';
import { ReferralsSection } from '@/components/provider-dashboard/ReferralsSection';
import { ProviderProfileSection } from '@/components/provider-dashboard/ProviderProfileSection';
import { SelfReferralQRCode } from '@/components/provider-dashboard/SelfReferralQRCode';
import { Pagination } from '@/components/ui/Pagination';
import { Card } from '@/components/ui/Card';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';
import { useReferrals } from '@/hooks/useReferrals';
import { useReferralsFilter } from '@/hooks/useReferralsFilter';
import { useTheme } from '@/hooks/useTheme';
import { apiService, getLogger } from '@/services';
import type { ApprovalRecord } from '@/services/domains/ApprovalDomain';
import type { ProviderQRCodeResponse } from '@/services/domains/SelfReferralDomain';
import { TitleText } from '../ui';

interface ProviderDashboardPageProps {
  className?: string;
}

export const ProviderDashboardPage: React.FC<ProviderDashboardPageProps> = ({
  className,
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [approvalRecord, setApprovalRecord] = useState<ApprovalRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [providerId, setProviderId] = useState<string>('');
  const [qrCodeData, setQrCodeData] = useState<ProviderQRCodeResponse | null>(null);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  const [qrCodeError, setQrCodeError] = useState<string>('');

  // Set page title
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = 'Provider Dashboard - BiteWise Healthcare Platform';
    }
  }, []);

  // Fetch provider profile and approval status on component mount
  useEffect(() => {
    const fetchProviderData = async () => {
      try {
        // First, fetch the provider profile
        const profile = await apiService.provider.getProfile();
        setProviderId(profile.user_id);

        // If profile fetch succeeds, continue with approval status
        const fetchApprovalStatus = async () => {
          try {
            const record = await apiService.approval.getLatestApprovalForType('provider');
            setApprovalRecord(record);
          } catch (error) {
            logger.error('Failed to fetch approval status:', error);
          } finally {
            setIsLoading(false);
          }
        };

        await fetchApprovalStatus();
      } catch (error) {
        logger.error('Failed to fetch provider profile:', error);
        // If profile fetch fails, navigate to provider registration
        navigate('/provider/register', { replace: true });
      }
    };

    fetchProviderData();
  }, [navigate]);

  const isApproved = import.meta.env.VITE_ENABLE_ADMIN === 'false' || approvalRecord?.status === 'approved';

  // Fetch QR code data when provider is approved
  useEffect(() => {
    const fetchQRCodeData = async () => {
      if (!isApproved || !providerId) return;

      setQrCodeLoading(true);
      setQrCodeError('');

      try {
        const qrData = await apiService.selfReferral.getProviderQRCode();
        setQrCodeData(qrData);
      } catch (error) {
        logger.error('Failed to fetch QR code data:', error);
        setQrCodeError('Failed to load QR code');
      } finally {
        setQrCodeLoading(false);
      }
    };

    fetchQRCodeData();
  }, [isApproved, providerId]);

  // Referrals filtering and management
  const {
    filterState,
    debouncedFilterState,
    updateSearch,
    updateStatusFilter,
    updateSort,
    updatePage,
  } = useReferralsFilter();

  const {
    referrals,
    photoUrls,
    pagination,
    loading: referralsLoading,
    error: referralsError
  } = useReferrals(debouncedFilterState);

  // Event handlers
  const handleNewReferralClick = useCallback(() => {
    // Only allow navigation if approved
    if (isApproved) {
      window.location.href = '/referral-workflow';
    }
  }, [isApproved]);

  const handleReferralClick = useCallback((referralId: string) => {
    // Navigate to referral details page
    window.location.href = `/referral-details?id=${referralId}`;
  }, []);

  const handleFilterChange = useCallback((newFilter: Partial<typeof filterState>) => {
    if (newFilter.searchTerm !== undefined) {
      updateSearch(newFilter.searchTerm);
    }
    if (newFilter.statusFilter !== undefined) {
      updateStatusFilter(newFilter.statusFilter);
    }
    if (newFilter.sortBy !== undefined) {
      updateSort(newFilter.sortBy, newFilter.sortOrder);
    }
    if (newFilter.page !== undefined) {
      updatePage(newFilter.page);
    }
  }, [updateSearch, updateStatusFilter, updateSort, updatePage]);

  return (
    <Layout className={className}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="provider-dashboard">
        {/* Page Header */}
        <div className="mb-8 mt-12">
          <TitleText>
            Welcome back
          </TitleText>
          <p className={`text-base mt-2 ${isDark ? 'text-gray-400' : 'text-secondary-600'}`}>
            Manage your patient referrals and track progress
          </p>
        </div>

        {/* Main Layout - Three Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Profile Section (1/4 width) */}
          <div className="lg:col-span-1">
            <ProviderProfileSection />
          </div>

          {/* Center Content - Main Dashboard Content (2/4 width) */}
          <div className="lg:col-span-2">
            {/* Refer New Patient CTA - Only show if approved */}
            {isApproved && (
              <div className="mb-6">
                <ReferNewPatientCTA
                  onReferClick={handleNewReferralClick}
                  loading={isLoading}
                  disabled={false}
                />
              </div>
            )}

            {/* Recent Referrals Section or Approval Pending */}
            <div className="mb-6">
              {isLoading ? (
                <Card className="p-6">
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                </Card>
              ) : isApproved ? (
                <ReferralsSection
                  referrals={referrals}
                  photoUrls={photoUrls}
                  filterState={filterState}
                  onFilterChange={handleFilterChange}
                  onReferralClick={handleReferralClick}
                  loading={referralsLoading}
                  error={referralsError}
                />
              ) : (
                <Card className="p-6 text-center">
                  <SubtitleText className="font-semibold mb-2">Account Approval Pending</SubtitleText>
                  <BodyText variant="muted" className="mb-4 px-8">
                    Your provider account is currently under review. You will receive an email notification in your inbox when your account has been approved.
                  </BodyText>
                </Card>
              )}
            </div>

            {/* Pagination - Only show when approved and have referrals */}
            {isApproved && !isLoading && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                itemsPerPage={pagination.pageSize}
                onPageChange={(page) => handleFilterChange({ page })}
                disabled={referralsLoading}
                showInfo
                showPageNumbers
              />
            )}
          </div>

          {/* Right Sidebar - Self Referral QR Code (1/4 width) */}
          <div className="lg:col-span-1">
            {isApproved && (
              <SelfReferralQRCode
                providerId={providerId}
                qrCodeData={qrCodeData}
                loading={qrCodeLoading}
                error={qrCodeError}
              />
            )}
          </div>
        </div>

        {/* Loading indicator for E2E tests */}
        {referralsLoading && (
          <div data-testid="dashboard-loading" className="sr-only">
            Loading dashboard data...
          </div>
        )}

        {/* Ready indicator for E2E tests */}
        {!referralsLoading && (
          <div data-testid="dashboard-ready" className="sr-only">
            Dashboard loaded
          </div>
        )}

      </div>
    </Layout>
  );
};

export default ProviderDashboardPage;